import {
  ActionIcon,
  Box,
  Flex,
  rem,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import React from "react";
import BotMessageSquareIcon from "../../../shared/svg/BotMessage";
import { IconPlus } from "../../../shared/svg/AddIcon";
import { useMediaQuery } from "@mantine/hooks";
import { Goal } from "../../../types/api";
import { AITooltip } from "../../../utils/components/AITooltip";

interface GoalItemProps {
  goal: Goal;
  height?: number;
  onClickTitle: (goal: Goal) => void;
  onClickAddTask: (goal: Goal) => void;
}

export const GoalItem: React.FC<GoalItemProps> = ({
  goal,
  height = 58,
  onClickTitle,
  onClickAddTask,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(pointer: coarse)");

  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: isMobile ? rem(300) : rem(400),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
  };

  const isTouchDevice =
    typeof window !== "undefined" && "ontouchstart" in window;

  return (
    <Flex
      key={goal.id}
      px={11}
      py={16}
      align="center"
      className="goal-item"
      miw="276px"
      h={height}
      justify="space-between"
      wrap="nowrap"
    >
      <Flex align="center" gap={8} style={{ overflow: "hidden", flex: 1 }}>
        {goal.autogenerated && (
          <AITooltip
            icon={
              <Box
                w={28}
                h={28}
                style={{
                  borderRadius: "50%",
                  backgroundColor: theme.colors.violet[1],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BotMessageSquareIcon
                  size={16}
                  color={theme.colors.violet[7]}
                />
              </Box>
            }
            title={t("generated_by_ai")}
            // zIndex={301}
          />
        )}
        <Tooltip
          openDelay={500}
          label={goal.title}
          position="top"
          offset={6}
          disabled={isTouchDevice}
          styles={{
            tooltip: {
              ...commonStyles,
              border: "1px solid gray",
            },
            arrow: {
              backgroundColor: theme.colors.gray[1],
              border: "1px solid gray",
            },
          }}
        >
          <Text
            style={{
              cursor: "pointer",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
            onClick={() => onClickTitle(goal)}
          >
            {goal.title}
          </Text>
        </Tooltip>
      </Flex>

      <Tooltip
        withArrow
        transitionProps={{ duration: 100, transition: "fade" }}
        arrowSize={6}
        arrowOffset={8}
        position="top"
        offset={6}
        styles={{
          tooltip: commonStyles,
          arrow: { backgroundColor: theme.colors.gray[1] },
        }}
        label={t("add_task_tooltip")}
      >
        <ActionIcon
          variant="subtle"
          radius="xl"
          onClick={() => onClickAddTask(goal)}
          style={{
            padding: "4px 8px",
            border: "none",
            color: "#737373",
            fontWeight: 700,
            fontSize: "24px",
            flexShrink: 0,
          }}
        >
          <IconPlus />
        </ActionIcon>
      </Tooltip>
    </Flex>
  );
};
